import { Poi, Tag } from '@/js/types'
import { FC, useMemo } from 'react'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible.tsx'
import { SidebarGroup, SidebarGroupContent, SidebarGroupLabel } from '@/components/ui/sidebar.tsx'
import { ChevronDown } from 'lucide-react'
import { PoiItem } from '@/js/editor/components/general/poi/PoiItem.tsx'
import { getContrastColor } from '@/js/helper/color.ts'
import { useAtomValue } from 'jotai'
import { mapDataAtom, tagsAtom } from '@/js/editor/state/mapAtoms.ts'

type PoiCategorizedListingProps = {
    pois?: Array<Poi>
    activePoi?: Poi
    itemAction?: (poi: Poi) => void
    flagged?: {
        condition: (poi: Poi) => boolean
        text: string
    }
    hoverAction?: (poi: Poi | null) => void
}

export const PoiCategorizedListing: FC<PoiCategorizedListingProps> = ({
    pois,
    activePoi,
    itemAction,
    flagged,
    hoverAction,
}) => {
    const tags = useAtomValue(tagsAtom)
    const { colors } = useAtomValue(mapDataAtom)
    const color = colors.primary ?? 'var(--color-mm-blue)'

    const contrastColor = useMemo(() => getContrastColor(color), [color])

    const taggedPois = tags.map((tag: Tag) => ({
        tag,
        pois: pois?.filter((poi) => poi.tags.some((poiTag) => poiTag.id === tag.id)) ?? [],
    }))

    const untaggedPois = {
        tag: { id: 'untagged', name: 'Untagged' },
        pois: pois?.filter((poi) => poi.tags.length === 0) ?? [],
    }

    const combinedPois = [...(untaggedPois.pois.length > 0 ? [untaggedPois] : []), ...taggedPois]
    return (
        <>
            {combinedPois.map(({ tag, pois }) => (
                <Collapsible key={tag.id} className="group/collapsible">
                    <SidebarGroup>
                        <SidebarGroupLabel asChild>
                            <CollapsibleTrigger
                                className="pl-4 pr-3 hover:text-black text-sm!"
                                style={{ color: contrastColor, backgroundColor: color }}
                            >
                                {tag.name}
                                <ChevronDown
                                    className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180"
                                    style={{ color: contrastColor }}
                                />
                            </CollapsibleTrigger>
                        </SidebarGroupLabel>
                        <CollapsibleContent>
                            <SidebarGroupContent>
                                {pois.length > 0 ? (
                                    pois.map((poi) => (
                                        <PoiItem
                                            poi={poi}
                                            key={poi.id}
                                            active={activePoi?.id === poi.id}
                                            action={(poi) => {
                                                itemAction?.(poi)
                                            }}
                                            flagged={flagged?.condition(poi) ? flagged?.text : undefined}
                                            onHover={(poi) => {
                                                hoverAction?.(poi)
                                            }}
                                        />
                                    ))
                                ) : (
                                    <div className={'text-mm-gray2 text-sm p-2 pl-5'}>Kein Eintrag vorhanden</div>
                                )}
                            </SidebarGroupContent>
                        </CollapsibleContent>
                    </SidebarGroup>
                </Collapsible>
            ))}
        </>
    )
}
